'use client';

import { HStack, VStack } from '@terra/ui-styled-system';
import { Badge } from '../../../../../components/badge';
import { Text } from '../../../../../components/styled/text';
import { formatCategoryForDisplay } from '../../../../../utils/category-utils';
import type { CardRenderContext } from '../../tool-config-types';
import {
  parseFindingData,
  parseFindingsListData,
  parseFindingsSearchData,
} from './FindingsRenderer';
import type { Finding } from './types';
import { InlineExecutionToolCard } from '../shared/inline-execution-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';

function parseFindingsOutput(output: unknown): {
  findings: Finding[];
  query?: string;
  totalResults?: number;
} {
  if (typeof output === 'object' && output !== null) {
    const searchData = parseFindingsSearchData(output);

    if (searchData) {
      return {
        findings: searchData.findings,
        query: searchData.query,
        totalResults: searchData.totalResults,
      };
    }

    const listData = parseFindingsListData(output);

    if (listData) {
      return {
        findings: listData,
      };
    }

    const oneFinding = parseFindingData(output as Partial<Finding>);

    if (oneFinding) {
      return {
        findings: [oneFinding],
      };
    }
  }

  return {
    findings: [],
  };
}

function FindingsFlatList({
  findings,
  onOpenObject,
}: {
  findings: Finding[];
  onOpenObject?: CardRenderContext['onOpenObject'];
}) {
  return (
    <VStack alignItems="stretch" gap={0.5} width="100%" overflowX="hidden">
      {findings.map((finding, index) => {
        const findingId = finding.findingId ?? finding.id;
        const title =
          finding.title ||
          finding.findingTitle ||
          finding.shortDescription ||
          'Finding';
        const key = findingId ?? `${title}-${index}`;
        const canOpen = Boolean(onOpenObject && findingId);

        return (
          <HStack
            key={key}
            gap={1.5}
            py={1}
            px={1.5}
            alignItems="center"
            borderRadius="sm"
            cursor={canOpen ? 'pointer' : 'default'}
            _hover={canOpen ? { bg: 'bg.subtle' } : undefined}
            onClick={
              canOpen && findingId
                ? () =>
                    onOpenObject?.({
                      type: 'finding',
                      id: findingId as string,
                      label: title,
                    })
                : undefined
            }
          >
            {finding.severity ? (
              <Badge size="sm" variant="outline">
                <Text fontSize="2xs">{finding.severity}</Text>
              </Badge>
            ) : null}
            <Text fontSize="xs" color="fg.default" truncate flex={1}>
              {title}
            </Text>
            {finding.category ? (
              <Text fontSize="2xs" color="fg.muted">
                {formatCategoryForDisplay(finding.category)}
              </Text>
            ) : null}
          </HStack>
        );
      })}
    </VStack>
  );
}

export function renderFindingsCard(ctx: CardRenderContext) {
  const isRunning =
    ctx.toolCallData.status === 'started' ||
    ctx.toolCallData.status === 'in-progress';
  const parsedOutput = parseFindingsOutput(ctx.toolCallData.output);
  const findings = parsedOutput.findings;
  const totalResults = parsedOutput.totalResults ?? findings.length;

  if (
    !isRunning &&
    findings.length === 0 &&
    !ctx.toolCallData.error &&
    ctx.toolCallData.status === 'completed'
  ) {
    return null;
  }

  const headerMeta =
    findings.length > 0 || parsedOutput.query ? (
      <HStack gap={1} flexShrink={0} minWidth={0}>
        <Text fontSize="2xs" color="fg.muted" flexShrink={0}>
          {totalResults}
        </Text>
        {parsedOutput.query ? (
          <Text fontSize="2xs" color="fg.muted" truncate maxW="180px">
            {parsedOutput.query}
          </Text>
        ) : null}
      </HStack>
    ) : undefined;

  return (
    <InlineExecutionToolCard
      title={ctx.toolName}
      icon={ctx.toolIcon}
      status={ctx.toolCallData.status}
      error={ctx.toolCallData.error}
      defaultExpanded={false}
      autoCollapseWhenComplete={ctx.autoCollapseWhenComplete}
      forceExpanded={ctx.forceExpanded}
      headerMeta={headerMeta}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    >
      {findings.length > 0 ? (
        <FindingsFlatList findings={findings} onOpenObject={ctx.onOpenObject} />
      ) : null}
    </InlineExecutionToolCard>
  );
}
